import API from "./api";

const DB_NAME = "ebek-one-expenses-offline";
const DB_VERSION = 1;
const STORE = "expense-requests";
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_QUEUE_BYTES = 50 * 1024 * 1024;

function openDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE, { keyPath: "client_sync_id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withStore(mode, operation) {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const request = operation(tx.objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

export const queuedExpenses = async () => withStore("readonly", store => store.getAll());

function currentUserId() {
  try { return String(JSON.parse(localStorage.getItem("user") || "null")?.id || ""); }
  catch { return ""; }
}

export async function queuedExpenseCount() {
  const userId = currentUserId();
  const rows = await queuedExpenses();
  return rows.filter(row => !row.user_id || String(row.user_id) === userId).length;
}

function requestFromFormData(formData, options = {}) {
  const fields = {};
  let bill = null;
  for (const [key, value] of formData.entries()) {
    if (key === "bill" && value instanceof Blob) bill = value;
    else fields[key] = String(value);
  }
  const clientSyncId = fields.client_sync_id || `expense-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  fields.client_sync_id = clientSyncId;
  return {
    client_sync_id: clientSyncId,
    user_id: String(options.userId || currentUserId()),
    fields,
    bill,
    bill_name: bill?.name || "offline-bill.jpg",
    bill_size: bill?.size || 0,
    queued_at: new Date().toISOString(),
    needs_approval: Boolean(options.needsApproval),
    last_error: null,
    attempts: 0,
  };
}

export async function queueExpense(formData, options = {}) {
  const record = requestFromFormData(formData, options);
  if (record.bill_size > MAX_FILE_BYTES) throw new Error("Bill must be 10 MB or smaller.");
  const current = await queuedExpenses();
  const used = current.reduce((sum, row) => sum + Number(row.bill_size || 0), 0);
  if (used + record.bill_size > MAX_QUEUE_BYTES) {
    throw new Error("Offline storage is full. Connect to upload pending bills first.");
  }
  await withStore("readwrite", store => store.put(record));
  window.dispatchEvent(new CustomEvent("offline-expenses-changed"));
  return (await queuedExpenseCount());
}

function toFormData(record) {
  const form = new FormData();
  Object.entries(record.fields || {}).forEach(([key, value]) => form.append(key, value));
  if (record.bill) form.append("bill", record.bill, record.bill_name || "offline-bill.jpg");
  return form;
}

async function removeExpense(clientSyncId) {
  await withStore("readwrite", store => store.delete(clientSyncId));
}

async function updateExpense(record) {
  await withStore("readwrite", store => store.put(record));
}

let activeFlush = null;

async function runFlushExpenses() {
  if (!navigator.onLine || !localStorage.getItem("token")) return { synced: 0, pending: await queuedExpenseCount(), failed: 0 };
  const rows = await queuedExpenses();
  const userId = currentUserId();
  let synced = 0;
  let alreadySaved = 0;
  let failed = 0;
  for (const row of rows) {
    if (row.user_id && String(row.user_id) !== userId) continue;
    if (row.retryable === false) { failed += 1; continue; }
    try {
      // Approval routing is part of the server-side save operation. Keeping it
      // there prevents a successfully saved expense from remaining in this
      // queue merely because a second approval request failed.
      let response;
      try {
        response = await API.post("/expenses", toFormData(row));
      } catch (error) {
        if (error.response?.status !== 409 || error.response?.data?.error !== "client_sync_conflict") throw error;
        const replacementId = `expense-${row.user_id || "user"}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const replacement = {
          ...row,
          client_sync_id: replacementId,
          fields: { ...(row.fields || {}), client_sync_id: replacementId },
        };
        await removeExpense(row.client_sync_id);
        await updateExpense(replacement);
        Object.assign(row, replacement);
        response = await API.post("/expenses", toFormData(replacement));
      }
      await removeExpense(row.client_sync_id);
      if (response?.data?.duplicate) alreadySaved += 1;
      else synced += 1;
    } catch (error) {
      const retryable = !error.response || error.response.status >= 500 || error.response.status === 429;
      const message = error.response?.data?.blockMessage || error.response?.data?.error || "Upload failed";
      await updateExpense({ ...row, attempts: Number(row.attempts || 0) + 1, last_error: String(message).slice(0, 240), retryable, last_attempt_at:new Date().toISOString() });
      failed += 1;
    }
  }
  const pending = await queuedExpenseCount();
  window.dispatchEvent(new CustomEvent("offline-expenses-changed", { detail: { synced, alreadySaved, pending, failed } }));
  return { synced, alreadySaved, pending, failed };
}

export function flushExpenses() {
  if (activeFlush) return activeFlush;
  activeFlush = runFlushExpenses().finally(() => { activeFlush = null; });
  return activeFlush;
}

export async function discardQueuedExpense(clientSyncId) {
  await removeExpense(clientSyncId);
  window.dispatchEvent(new CustomEvent("offline-expenses-changed"));
}

export async function retryQueuedExpense(clientSyncId) {
  const rows = await queuedExpenses();
  const row = rows.find(item => item.client_sync_id === clientSyncId);
  if (!row) return;
  await updateExpense({ ...row, retryable: true, last_error: null });
  window.dispatchEvent(new CustomEvent("offline-expenses-changed"));
  return flushExpenses();
}
